import jsPDF from 'jspdf';
import { DEFAULT_BRANDING, mergeBrandingConfig, colorUtils } from '../templates/pdfBrandingTemplate';
import { createThemeBrandingConfig, getThemeDisplayName } from './themeColorMapper';
import { getRayDisplayName, getTypeDisplayName, getElementDisplayName } from '../constants/oracleMysticalMappings';

class PDFGenerator {
  constructor() {
    this.doc = null;
    this.currentY = 0;
    this.pageHeight = 297; // A4 height in mm
    this.pageWidth = 210; // A4 width in mm
    this.margin = 20;
    this.contentWidth = this.pageWidth - (this.margin * 2);
    this.brandingConfig = null;
  }

  // Initialize PDF with branding configuration  
  init(brandingConfig = null, themeId = 'basic') {
    this.doc = new jsPDF('p', 'mm', 'a4');
    
    // Create theme-aware branding if no custom config provided
    if (!brandingConfig) {
      this.brandingConfig = createThemeBrandingConfig(themeId);
    } else {
      this.brandingConfig = mergeBrandingConfig(brandingConfig);
    }
    
    // Set proper page dimensions and margins
    this.pageHeight = 297;
    this.pageWidth = 210;
    this.margin = this.brandingConfig.layout.margins.left; // Use left margin as base
    this.contentWidth = this.pageWidth - (this.brandingConfig.layout.margins.left + this.brandingConfig.layout.margins.right);
    this.currentY = this.brandingConfig.layout.margins.top;
    
    return this;
  }

  // Add page header with branding
  addHeader(fontScale = 1.0) {
    const headerHeight = 40 * fontScale;
    const leftMargin = this.brandingConfig.layout.margins.left;
    const rightMargin = this.brandingConfig.layout.margins.right;
    
    if (this.brandingConfig.branding.logoUrl) {
      // Future: Add logo image
    }

    // Title
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.title * fontScale);
    const [r, g, b] = colorUtils.hexToRgb(this.brandingConfig.colors.primary);
    this.doc.setTextColor(r, g, b);
    this.doc.text(this.brandingConfig.branding.title, leftMargin, this.currentY + (10 * fontScale));
    
    // Subtitle
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.subtitle * fontScale);
    const [sr, sg, sb] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(sr, sg, sb);
    this.doc.text(this.brandingConfig.branding.subtitle, leftMargin, this.currentY + (20 * fontScale));
    
    // Separator line
    const [ar, ag, ab] = colorUtils.hexToRgb(this.brandingConfig.colors.accent);
    this.doc.setDrawColor(ar, ag, ab);
    this.doc.setLineWidth(0.5);
    this.doc.line(leftMargin, this.currentY + (30 * fontScale), this.pageWidth - rightMargin, this.currentY + (30 * fontScale));
    
    this.currentY += headerHeight;
    return this;
  }

  // Add header for continuation pages
  addContinuationPageHeader() {
    const leftMargin = this.brandingConfig.layout.margins.left;
    const rightMargin = this.brandingConfig.layout.margins.right;
    const topMargin = this.brandingConfig.layout.margins.top;
    
    // Title
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.subtitle);
    const [r, g, b] = colorUtils.hexToRgb(this.brandingConfig.colors.primary);
    this.doc.setTextColor(r, g, b);
    this.doc.text(this.brandingConfig.branding.title, leftMargin, topMargin + 12);
    
    // Subtitle
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.body);
    const [sr, sg, sb] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(sr, sg, sb);
    this.doc.text(this.brandingConfig.branding.subtitle, leftMargin, topMargin + 22);
    
    // Separator line
    const [ar, ag, ab] = colorUtils.hexToRgb(this.brandingConfig.colors.accent);
    this.doc.setDrawColor(ar, ag, ab);
    this.doc.setLineWidth(0.3);
    this.doc.line(leftMargin, topMargin + 30, this.pageWidth - rightMargin, topMargin + 30);
  }

  // Add session info section
  addSessionInfo(sessionData, fontScale = 1.0) {
    this.checkPageBreak(40 * fontScale);
    const leftMargin = this.brandingConfig.layout.margins.left;
    
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.sectionTitle * fontScale);
    const [r, g, b] = colorUtils.hexToRgb(this.brandingConfig.colors.primary);
    this.doc.setTextColor(r, g, b);
    this.doc.text('Informações da Sessão', leftMargin, this.currentY);
    this.currentY += this.brandingConfig.layout.spacing.paragraphGap * fontScale;

    this.doc.setFontSize(this.brandingConfig.fonts.sizes.caption * fontScale);
    const [tr, tg, tb] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(tr, tg, tb);
    
    const sessionInfo = [
      `Data da Consulta: ${this.formatDate(sessionData.completedAt || sessionData.timestamp)}`,
      `Raio Selecionado: ${getRayDisplayName(sessionData.raySelection)}`,
      // TD3-105: Theme display hidden per beta testing feedback - will reactivate in Phase 3
      // `Tema: ${sessionData.themeId || 'Basic'}`,
      `Cartas Diagnóstico: ${getTypeDisplayName(sessionData.diagnosisType)}, Elemento ${getElementDisplayName(sessionData.diagnosisElement)}`,
      `Cartas Solução: ${getTypeDisplayName(sessionData.solutionType)}, Elemento ${getElementDisplayName(sessionData.solutionElement)}`
    ];

    sessionInfo.forEach(info => {
      // Wrap text to fit within margins
      const wrappedLines = this.doc.splitTextToSize(info, this.contentWidth - 5);
      wrappedLines.forEach(line => {
        this.doc.text(line, leftMargin + 5, this.currentY);
        this.currentY += this.brandingConfig.layout.spacing.lineHeight * fontScale;
      });
    });

    this.currentY += this.brandingConfig.layout.spacing.paragraphGap * fontScale;
    return this;
  }

  // Add user question section
  addUserQuestion(question, fontScale = 1.0) {
    this.checkPageBreak(30 * fontScale);
    const leftMargin = this.brandingConfig.layout.margins.left;
    
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.sectionTitle * fontScale);
    const [r, g, b] = colorUtils.hexToRgb(this.brandingConfig.colors.primary);
    this.doc.setTextColor(r, g, b);
    this.doc.text('Sua Pergunta', leftMargin, this.currentY);
    this.currentY += 10 * fontScale;

    this.doc.setFontSize(this.brandingConfig.fonts.sizes.body * fontScale);
    const [tr, tg, tb] = colorUtils.hexToRgb(this.brandingConfig.colors.text);
    this.doc.setTextColor(tr, tg, tb);
    this.addWrappedText(`"${question}"`, { italic: true }, fontScale);
    
    this.currentY += 15 * fontScale;
    return this;
  }

  // Add content section (blockage, solution, wisdom, decree)
  addContentSection(title, content, isDecree = false, fontScale = 1.0) {
    if (!content) return this;
    
    const estimatedHeight = (this.estimateTextHeight(content) + 25) * fontScale;
    this.checkPageBreak(estimatedHeight);
    const leftMargin = this.brandingConfig.layout.margins.left;
    
    // Section title
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.sectionTitle * fontScale);
    const [r, g, b] = colorUtils.hexToRgb(this.brandingConfig.colors.primary);
    this.doc.setTextColor(r, g, b);
    this.doc.text(title, leftMargin, this.currentY);
    this.currentY += 12 * fontScale;

    // Content text
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.body * fontScale);
    const [tr, tg, tb] = colorUtils.hexToRgb(this.brandingConfig.colors.text);
    this.doc.setTextColor(tr, tg, tb);
    
    if (isDecree) {
      this.addWrappedText(`"${content}"`, { italic: true, bold: true }, fontScale);
      this.currentY += 5 * fontScale;
      this.doc.setFontSize(this.brandingConfig.fonts.sizes.caption * fontScale);
      const [ir, ig, ib] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
      this.doc.setTextColor(ir, ig, ib);
      const instructionLines = this.doc.splitTextToSize('Repita este decreto diariamente para fortalecer sua transformação', this.contentWidth - 10);
      instructionLines.forEach(line => {
        this.doc.text(line, leftMargin + 5, this.currentY);
        this.currentY += this.brandingConfig.layout.spacing.lineHeight * fontScale;
      });
    } else {
      this.addWrappedText(content, {}, fontScale);
    }
    
    this.currentY += 20 * fontScale;
    return this;
  }

  // Add footer with branding
  addFooter(fontScale = 1.0) {
    const footerY = this.pageHeight - (20 * fontScale);
    const leftMargin = this.brandingConfig.layout.margins.left;
    const rightMargin = this.brandingConfig.layout.margins.right;
    
    this.doc.setFontSize(this.brandingConfig.fonts.sizes.caption * fontScale);
    const [fr, fg, fb] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(fr, fg, fb);
    
    // Left side - generation info
    this.doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, leftMargin, footerY);
    
    // Right side - branding or page number
    const footerText = this.brandingConfig.branding.footerText || 'Oráculo dos 12 Raios';
    const textWidth = this.doc.getTextWidth(footerText);
    this.doc.text(footerText, this.pageWidth - rightMargin - textWidth, footerY);
    
    return this;
  }

  // Utility: Check if content fits on current page
  checkPageBreak(requiredSpace) {
    if (this.currentY + requiredSpace > this.pageHeight - 30) {
      this.doc.addPage();
      // Add gradient background to new page
      this.addGradientBackground();
      // Add header to continuation pages
      this.addContinuationPageHeader();
      this.currentY = this.brandingConfig.layout.margins.top + 40; // Account for header space
    }
  }

  // Utility: Add wrapped text that fits within page width
  addWrappedText(text, options = {}, fontScale = 1.0) {
    const maxWidth = this.contentWidth - 10; // Leave some margin
    const leftMargin = this.brandingConfig.layout.margins.left;
    const lineHeight = this.brandingConfig.layout.spacing.lineHeight * fontScale;
    
    if (options.bold) this.doc.setFont(undefined, 'bold');
    if (options.italic) this.doc.setFont(undefined, 'italic');
    
    const lines = this.doc.splitTextToSize(text, maxWidth);
    lines.forEach(line => {
      this.checkPageBreak(lineHeight);
      this.doc.text(line, leftMargin + 5, this.currentY);
      this.currentY += lineHeight;
    });
    
    // Reset font
    this.doc.setFont(undefined, 'normal');
  }

  // Utility: Estimate text height for page break calculations
  estimateTextHeight(text) {
    const maxWidth = this.contentWidth - 10;
    const lines = this.doc.splitTextToSize(text, maxWidth);
    return lines.length * 6;
  }

  // Utility: Format date for display
  formatDate(dateString) {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Estimate total content length for dynamic font sizing
  estimateTotalContentLength(sessionData) {
    let totalLength = 0;
    
    if (sessionData.userQuestion) totalLength += sessionData.userQuestion.length;
    if (sessionData.blockageText) totalLength += sessionData.blockageText.length;
    if (sessionData.unblockText) totalLength += sessionData.unblockText.length;
    if (sessionData.wisdomText) totalLength += sessionData.wisdomText.length;
    if (sessionData.decreeText) totalLength += sessionData.decreeText.length;
    
    return totalLength;
  }

  // Calculate font scale based on content length (target max 2 pages)
  calculateFontScale(totalContentLength) {
    // Base scale for ~2000 characters fitting in 2 pages
    const baseLength = 2000;
    const maxScale = 1.0;
    const minScale = 0.8;
    
    if (totalContentLength <= baseLength) {
      return maxScale;
    }
    
    // Reduce font size for longer content
    const scale = Math.max(minScale, baseLength / totalContentLength);
    return Math.min(maxScale, scale);
  }

  // Generate complete oracle PDF
  generateOraclePDF(sessionData, brandingConfig = null) {
    const themeId = sessionData.themeId || 'basic';
    this.init(brandingConfig, themeId);
    
    // Calculate total content length for dynamic font sizing
    const totalContentLength = this.estimateTotalContentLength(sessionData);
    const fontScale = this.calculateFontScale(totalContentLength);
    
    // Add subtle gradient background
    this.addGradientBackground();
    
    // Build PDF content with dynamic sizing
    this.addHeader(fontScale)
        .addSessionInfo(sessionData, fontScale)
        .addUserQuestion(sessionData.userQuestion, fontScale);

    // Add oracle content sections
    if (sessionData.blockageText) {
      this.addContentSection('Seu Bloqueio', sessionData.blockageText, false, fontScale);
    }
    
    if (sessionData.unblockText) {
      this.addContentSection('Para Desbloquear', sessionData.unblockText, false, fontScale);
    }
    
    if (sessionData.wisdomText) {
      this.addContentSection('Sabedoria Ancestral', sessionData.wisdomText, false, fontScale);
    }
    
    if (sessionData.decreeText) {
      this.addContentSection('Seu Decreto Pessoal', sessionData.decreeText, true, fontScale);
    }

    this.addFooter(fontScale);
    
    return this.doc;
  }

  // Generate filename in format: LiviaBueno_Oracle_YYMMDD_HHMM
  generateFilename(prefix = 'LiviaBueno_Oracle', extension = '.pdf') {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hour = now.getHours().toString().padStart(2, '0');
    const minute = now.getMinutes().toString().padStart(2, '0');
    
    return `${prefix}_${year}${month}${day}_${hour}${minute}${extension}`;
  }

  // Download PDF
  downloadPDF(sessionData, customFilename = null, brandingConfig = null) {
    const pdf = this.generateOraclePDF(sessionData, brandingConfig);
    const filename = customFilename || this.generateFilename();
    pdf.save(filename);
  }

  // Get PDF as blob for further processing
  getPDFBlob(sessionData, brandingConfig = null) {
    const pdf = this.generateOraclePDF(sessionData, brandingConfig);
    return pdf.output('blob');
  }

  // Generate double A5 decree printout stacked vertically on A4 portrait
  generateDecreePrintout(sessionData, brandingConfig = null) {
    if (!sessionData.decreeText) {
      throw new Error('Decree text not available for printout');
    }

    const themeId = sessionData.themeId || 'basic';
    
    // Create A4 portrait PDF (210mm x 297mm) to fit two A5 sections stacked
    this.doc = new jsPDF('p', 'mm', 'a4'); // portrait orientation
    this.pageHeight = 297;
    this.pageWidth = 210;
    
    // Initialize with theme-aware branding
    if (!brandingConfig) {
      this.brandingConfig = createThemeBrandingConfig(themeId);
    } else {
      this.brandingConfig = mergeBrandingConfig(brandingConfig);
    }

    // Add subtle gradient background
    this.addGradientBackground();

    // Create two A5 sections stacked vertically (each 148.5mm height with cutting line)
    const sectionHeight = (this.pageHeight - 1) / 2; // Subtract 1mm for cutting line
    
    // Top A5 section (landscape orientation within portrait page)
    this.addA5DecreeSection(sessionData, 0, 0, this.pageWidth, sectionHeight, 'top');
    
    // Horizontal cutting line with scissors icon
    this.addHorizontalCuttingGuide();
    
    // Bottom A5 section (landscape orientation within portrait page)
    this.addA5DecreeSection(sessionData, 0, sectionHeight + 1, this.pageWidth, sectionHeight, 'bottom');

    return this.doc;
  }

  // Add gradient background with intense color on far right fading left
  addGradientBackground() {
    const [color1] = this.brandingConfig.gradients?.decree || this.brandingConfig.gradients?.header || ['#F8FAFC'];
    
    // Create gradient from right edge (intense) fading left (25% width)
    const gradientStartX = this.pageWidth * 0.75; // Start fade at 75% from left
    const gradientWidth = this.pageWidth * 0.25; // 25% width
    
    // Create multiple rectangles with increasing opacity towards the right
    const steps = 20;
    const stepWidth = gradientWidth / steps;
    
    for (let i = 0; i < steps; i++) {
      const x = gradientStartX + (i * stepWidth);
      const opacity = (i + 1) / steps * 0.15; // Increasing opacity towards right, max 15%
      
      const [r, g, b] = colorUtils.hexToRgb(color1);
      this.doc.setFillColor(r, g, b);
      this.doc.setGState(new this.doc.GState({ opacity: opacity }));
      this.doc.rect(x, 0, stepWidth, this.pageHeight, 'F');
    }
    
    // Reset opacity
    this.doc.setGState(new this.doc.GState({ opacity: 1 }));
  }


  // Add single A5 decree section (landscape orientation within portrait page)
  addA5DecreeSection(sessionData, offsetX, offsetY, sectionWidth, sectionHeight, position) {
    const centerX = offsetX + (sectionWidth / 2);
    const margin = 15;
    const contentWidth = sectionWidth - (margin * 2);
    
    // Footer in top-right corner first (reversed priority)
    const topRightX = offsetX + sectionWidth - margin;
    const topRightY = offsetY + 12;
    this.doc.setFontSize(6);
    const [fr, fg, fb] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(fr, fg, fb);
    
    // Date and footer text in top-right
    // TD3-105: Theme display removed per beta testing feedback - will reactivate in Phase 3
    const footerInfo = `${this.formatDate(sessionData.completedAt || sessionData.timestamp)} • ${this.brandingConfig.branding.footerText}`;
    this.doc.text(footerInfo, topRightX, topRightY, { align: 'right' });
    
    // Section header positioned closer to top with reduced spacing
    let currentY = offsetY + 25; // Reduced from 20
    
    // Title - smaller and closer together
    this.doc.setFontSize(18); // Reduced from 20
    const [r, g, b] = colorUtils.hexToRgb(this.brandingConfig.colors.primary);
    this.doc.setTextColor(r, g, b);
    this.doc.text('Seu Decreto Pessoal', centerX, currentY, { align: 'center' });
    
    // Subtitle - closer to title
    this.doc.setFontSize(11); // Reduced from 12
    const [sr, sg, sb] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(sr, sg, sb);
    this.doc.text('Oráculo Chama Violeta', centerX, currentY + 12, { align: 'center' }); // Reduced gap from 28 to 12
    
    // Decorative line - closer to subtitle
    const [ar, ag, ab] = colorUtils.hexToRgb(this.brandingConfig.colors.accent);
    this.doc.setDrawColor(ar, ag, ab);
    this.doc.setLineWidth(0.5);
    const lineWidth = Math.min(80, contentWidth * 0.6);
    this.doc.line(centerX - lineWidth/2, currentY + 20, centerX + lineWidth/2, currentY + 20); // Reduced gap from 38 to 20
    
    currentY += 28; // Reduced from 50
    
    // Decree content box - more space available
    const availableHeight = sectionHeight - 50; // Reduced reserved space from 80 to 50
    const estimatedBoxHeight = this.estimateA5DecreeBoxHeight(sessionData.decreeText, contentWidth);
    const boxHeight = Math.min(estimatedBoxHeight, availableHeight - 15); // Reduced margin
    const boxY = currentY + 5; // Reduced gap
    
    // Background box
    const bgColor = this.brandingConfig.sections.decree.backgroundColor || this.brandingConfig.colors.background;
    const [bgr, bgg, bgb] = colorUtils.hexToRgb(bgColor);
    this.doc.setFillColor(bgr, bgg, bgb);
    this.doc.setDrawColor(200, 200, 200);
    this.doc.setLineWidth(0.3);
    this.doc.roundedRect(offsetX + margin, boxY - 5, contentWidth, boxHeight + 15, 6, 6, 'FD'); // Reduced padding
    
    // Decree text with better spacing
    this.doc.setFontSize(13); // Slightly smaller for better fit
    this.doc.setFont(undefined, 'italic');
    const [dr, dg, db] = colorUtils.hexToRgb(this.brandingConfig.sections.decree.titleColor || this.brandingConfig.colors.primary);
    this.doc.setTextColor(dr, dg, db);
    
    const lines = this.doc.splitTextToSize(`"${sessionData.decreeText}"`, contentWidth - 10);
    const lineHeight = 6.5; // Slightly reduced line height
    let textY = boxY + 8; // Start closer to box top
    
    // More generous line calculation
    const maxLines = Math.floor((boxHeight - 10) / lineHeight); // Less reserved space
    const displayLines = lines.slice(0, maxLines);
    
    displayLines.forEach(line => {
      this.doc.text(line, centerX, textY, { align: 'center' });
      textY += lineHeight;
    });
    
    // Reset font
    this.doc.setFont(undefined, 'normal');
    
    // Instruction text - positioned after decree text with proper spacing
    const instructionY = Math.min(textY + 8, offsetY + sectionHeight - 15); // Ensure it fits
    this.doc.setFontSize(8); // Slightly smaller
    const [ir, ig, ib] = colorUtils.hexToRgb(this.brandingConfig.colors.textSecondary);
    this.doc.setTextColor(ir, ig, ib);
    this.doc.text('Repita este decreto diariamente', centerX, instructionY, { align: 'center' });
  }

  // Add horizontal cutting guide with scissors icon and dashed line
  addHorizontalCuttingGuide() {
    const centerY = this.pageHeight / 2;
    const lineColor = [180, 180, 180]; // Light gray
    
    // Dashed cutting line (horizontal)
    this.doc.setDrawColor(...lineColor);
    this.doc.setLineWidth(0.3);
    this.doc.setLineDashPattern([2, 2], 0); // 2mm dash, 2mm gap
    this.doc.line(0, centerY, this.pageWidth, centerY);
    
    // Reset line pattern
    this.doc.setLineDashPattern([], 0);
    
    // Scissors icons at page edges ON the line
    this.doc.setFontSize(10);
    this.doc.setTextColor(...lineColor);
    
    // Left edge scissors
    this.doc.text('✂', 5, centerY + 2, { align: 'left' });
    
    // Right edge scissors (rotated)
    this.doc.text('✂', this.pageWidth - 5, centerY + 2, { align: 'right' });
  }

  // Estimate box height for A5 decree content
  estimateA5DecreeBoxHeight(text, width) {
    const lines = this.doc.splitTextToSize(`"${text}"`, width - 10);
    return lines.length * 7 + 20; // 7mm per line + padding
  }

  // Download decree printout
  downloadDecreePrintout(sessionData, customFilename = null, brandingConfig = null) {
    const pdf = this.generateDecreePrintout(sessionData, brandingConfig);
    const filename = customFilename || this.generateFilename('LiviaBueno_Decreto');
    pdf.save(filename);
  }
}

// Export singleton instance
const pdfGenerator = new PDFGenerator();
export default pdfGenerator;